/**
 * Calendar badge settings (Settings > My Business > Advanced Settings), on
 * the salon record:
 *
 * - Toggles: one boolean column per badge (`show_badge_*`, all default
 *   true). A missing field (backend not deployed yet, or an older cached
 *   salon) reads as true.
 * - Display modes: None / Badge / Badge + Label for the New client and Any
 *   Available badges (`badge_display_*`). "Label" adds a short word beside
 *   the chip's icon ("New", "Any") wherever the event has room. New client
 *   supersedes the `show_badge_new_client` toggle (the backend keeps the two
 *   in sync); a missing mode falls back to that toggle (New client) or
 *   "none" (Any Available).
 *
 * Always-on badges (requested, confirmed, unconfirmed, arrived, forms
 * missing) have no setting.
 */

export type BadgeSettingKey =
  | "show_badge_no_show_history"
  | "show_badge_loyal"
  | "show_badge_pinned_notes"
  | "show_badge_opt_out_reminder"
  | "show_badge_opt_out_marketing"
  | "show_badge_campaign";

export type BadgeDisplayMode = "none" | "badge" | "label";

export type BadgeDisplayKey =
  | "badge_display_new_client"
  | "badge_display_any_available";

export type BadgeSettings = Record<BadgeSettingKey, boolean> &
  Record<BadgeDisplayKey, BadgeDisplayMode>;

interface SettingField<K> {
  key: K;
  label: string;
  info: string;
}

/** Display-mode settings, in Settings order. */
export const BADGE_DISPLAY_FIELDS: SettingField<BadgeDisplayKey>[] = [
  {
    key: "badge_display_new_client",
    label: "New client badge",
    info: "Marks a client's first appointment with you. With a label, the badge also reads “New” when the event has room.",
  },
  {
    key: "badge_display_any_available",
    label: "Any Available badge",
    info: "Marks appointments booked with the “Any Available” option. With a label, the badge also reads “Any” when the event has room.",
  },
];

/** The None / Badge / Badge + Label choices (Select data). */
export const BADGE_DISPLAY_MODE_OPTIONS: {
  value: BadgeDisplayMode;
  label: string;
}[] = [
  { value: "none", label: "Show None" },
  { value: "badge", label: "Show Badge" },
  { value: "label", label: "Show Badge + Label" },
];

export const badgeDisplayModeLabel = (mode: BadgeDisplayMode): string =>
  BADGE_DISPLAY_MODE_OPTIONS.find((o) => o.value === mode)?.label ?? "";

/** Toggle settings, in Settings order, with the label and a one-line
 *  explanation. */
export const BADGE_SETTING_FIELDS: SettingField<BadgeSettingKey>[] = [
  {
    key: "show_badge_no_show_history",
    label: "No-show history badge",
    info: "Marks clients with a no-show in the last 12 months.",
  },
  {
    key: "show_badge_loyal",
    label: "Loyal client badge",
    info: "Marks clients with 5 or more completed visits.",
  },
  {
    key: "show_badge_pinned_notes",
    label: "Pinned notes badge",
    info: "Marks clients with pinned notes on their profile.",
  },
  {
    key: "show_badge_opt_out_reminder",
    label: "Opted out of reminders badge",
    info: "Marks clients who opted out of reminders and transactional texts.",
  },
  {
    key: "show_badge_opt_out_marketing",
    label: "Opted out of marketing badge",
    info: "Marks clients who opted out of marketing messages.",
  },
  {
    key: "show_badge_campaign",
    label: "Campaign badge",
    info: "Marks appointments booked from a marketing campaign.",
  },
];

const isDisplayMode = (v: unknown): v is BadgeDisplayMode =>
  v === "none" || v === "badge" || v === "label";

/** A mode missing from the salon: New client follows the legacy toggle. */
const fallbackMode = (
  key: BadgeDisplayKey,
  s: Record<string, unknown>,
): BadgeDisplayMode => {
  if (key === "badge_display_any_available") return "none";
  return s.show_badge_new_client === false ? "none" : "badge";
};

/** Salon record → badge settings (toggles: missing / non-boolean → true). */
export const badgeSettingsFrom = (salon: unknown): BadgeSettings => {
  const s = (salon ?? {}) as Record<string, unknown>;
  return {
    ...Object.fromEntries(
      BADGE_SETTING_FIELDS.map(({ key }) => [key, s[key] !== false]),
    ),
    ...Object.fromEntries(
      BADGE_DISPLAY_FIELDS.map(({ key }) => [
        key,
        isDisplayMode(s[key]) ? s[key] : fallbackMode(key, s),
      ]),
    ),
  } as BadgeSettings;
};

/** Badge settings → PUT /business-locations fields (real booleans and valid modes; the
 *  API 400s otherwise). */
export const badgeSettingsPayload = (values: BadgeSettings): BadgeSettings =>
  ({
    ...Object.fromEntries(
      BADGE_SETTING_FIELDS.map(({ key }) => [key, values[key] === true]),
    ),
    ...Object.fromEntries(
      BADGE_DISPLAY_FIELDS.map(({ key }) => [
        key,
        isDisplayMode(values[key]) ? values[key] : fallbackMode(key, {}),
      ]),
    ),
  }) as BadgeSettings;
